import { ChangeDetectionStrategy, Component, DoCheck, Input, OnInit, ViewChild, inject } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ActionIconDef, Grid as InavistaGrid, GridActionIconClickEvent, GridConfig } from '@inavista/components/grid';
import { UserRoles } from 'projects/service/src/app/shared/models/app.model';
import { ClaimStatuses } from 'projects/service/src/app/shared/models/claim.model';
import { CommonService } from 'projects/service/src/app/shared/services/common.service';
import { exportInavistaGrid } from 'projects/service/src/app/shared/utils/inavista-grid-export';
import { LocalRowsGridApi, provideLocalGridRows } from 'projects/service/src/app/shared/utils/local-rows-grid-api';
import { statusPillColor } from 'projects/service/src/app/shared/utils/status-pill-colors';
import {
  buildCustomerRulesActionIcons, buildCustomerRulesGridConfig, CUSTOMER_RULES_COLUMNS, RULE_FAILED_ACTION, RULE_PASSED_ACTION,
} from '../isc-claims-rules.grid.config';

// Claim popup → Rules → Customer Rules (national accounts): the claim's customer rules, marked Passed or Failed by a
// CSA while the claim is In Progress, and saved with the claim (the popup's Save).
@Component({
    selector: 'app-isc-claims-customer-rules-tab',
    templateUrl: './isc-claims-customer-rules.component.html',
    styleUrls: ['./isc-claims-customer-rules.component.scss'],
    // The grid reads the claim form's rules, not an endpoint.
    providers: [...provideLocalGridRows()],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class IscClaimsRulesCustomerComponent implements OnInit, DoCheck {
  private readonly commonService = inject(CommonService);
  private readonly gridRows = inject(LocalRowsGridApi);

  @Input() claimForm: UntypedFormGroup;
  @ViewChild('customerRulesGrid') grid: InavistaGrid;

  gridConfig: GridConfig;
  actionIcons: ActionIconDef[] = [];
  private shownRules: any[] = null;

  ngOnInit(): void {
    this.gridRows.columns.set(LocalRowsGridApi.schema(CUSTOMER_RULES_COLUMNS));
    this.gridConfig = buildCustomerRulesGridConfig({
      canExport: !!this.commonService.roleClaims['AllGrid_Toolbar_Excel_Export']?.visible,
      statusColor: statusPillColor(s => this.commonService.getClaimsRuleStatus(s)),
    });
    this.actionIcons = buildCustomerRulesActionIcons(() => this.lockReason());
    this.refreshRows();
  }

  // The popup replaces the rules array when the claim loads (the old grid was bound to it).
  ngDoCheck(): void {
    if (this.gridConfig && this.rules() !== this.shownRules) this.refreshRows();
  }

  private rules(): any[] {
    return this.claimForm?.value?.iscCustomerRules ?? [];
  }

  private refreshRows() {
    this.shownRules = this.rules();
    this.gridRows.rows.set(this.shownRules.map((rule, i) => ({ ...rule, __rowKey: i })));
    this.grid?.refresh();
  }

  // Same rule as before: a CSA, on a claim In Progress.
  private lockReason(): string | null {
    if (this.commonService.USER.role !== UserRoles.csa) return 'Only a CSA can mark customer rules.';
    if (this.claimForm?.value?.status !== ClaimStatuses.inProgress) return 'Customer rules can be marked while the claim is In Progress.';
    return null;
  }

  // As before, the status is written on the rule in the claim form's value, which the popup's Save sends.
  onActionIconClick(event: GridActionIconClickEvent) {
    if (this.lockReason()) return;
    const rule = this.shownRules[event.row['__rowKey'] as number];
    if (!rule) return;
    if (event.iconKey === RULE_PASSED_ACTION) rule.status = 'Passed';
    else if (event.iconKey === RULE_FAILED_ACTION) rule.status = 'Failed';
    else return;
    this.refreshRows();
  }

  // Same file as before: the rows the grid shows, the visible columns, "Claims Rules(<date>).xlsx".
  onExportRequested() {
    exportInavistaGrid(this.grid, 'Claims Rules');
  }
}
